import React, { memo, useEffect, useState } from 'react';
import Button from '../../../Button/Button';
import { SearchOutlined } from '@ant-design/icons';
import styles from './CheckboxModel.module.scss'; // Keep if needed
import CustomSelect from '../../CustomSelect/CustomSelect';
import { useSelector } from 'react-redux';
import Loader from '../../LoadingSpinner/LoadingSpinner';

const CheckboxModel = ({
        isOpen,
        onClose,
        title = 'Modal',
        initialValues = {},
        onSubmit,
        labelHeading,
        placeholder,
        options = [], // Pass options dynamically
}) => {
        const { packages, loading } = useSelector((state) => state.package);

        const [selectedPackages, setSelectedPackages] = useState(initialValues.packageTypes || []);
        const [selectedOption, setSelectedOption] = useState(''); // Default selected
        const [showOptions, setShowOptions] = useState(false);
        const [searchTerm, setSearchTerm] = useState('');
        const [filteredPackages, setFilteredPackages] = useState([]);
        const handleCheckboxChange = (e, packageObj) => {
                setSelectedPackages(
                        (prev) =>
                                e.target.checked
                                        ? [...prev, packageObj] // Store whole object
                                        : prev.filter(
                                                  (p) => p.PACKAGE_ID !== packageObj.PACKAGE_ID,
                                          ), // Remove based on unique code
                );
        };

        const handleSubmit = () => {
                if (selectedPackages.length === 0 || !selectedOption) return;

                onSubmit({
                        packageTypes: selectedPackages,
                        unitCommercial: selectedOption,
                });
                setShowOptions(false);
                onClose();
        };

        const handleClose = () => {
                setShowOptions(false); // Reset dropdown when modal closes
                onClose(); // Call parent-provided close handler
        };

        useEffect(() => {
                const newPackages = packages.filter(
                        (pkg) =>
                                pkg.PACKAGE_NAME.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                pkg.PACKAGE_TYPE.toLowerCase().includes(searchTerm.toLowerCase()),
                );

                setFilteredPackages(searchTerm ? newPackages : packages);
        }, [searchTerm, packages]);

        useEffect(() => {
                setSelectedPackages(initialValues.packageTypes || []);
                setSelectedOption(''); // Set default selected when modal opens
        }, [initialValues.packageTypes, isOpen]);

        return (
                <div className="fixed inset-0 flex items-center justify-end bg-gray-800 bg-opacity-50 z-20">
                        <div className="bg-white rounded-2xl shadow-lg w-[650px] flex flex-col max-h-[90vh]">
                                {/* Header */}
                                <div className="flex justify-between items-center border-b px-6 py-4 rounded-t-2xl">
                                        <h2 className="text-[18px] font-semibold">{title}</h2>
                                        <button
                                                onClick={handleClose}
                                                aria-label="Close Modal">
                                                ✖
                                        </button>
                                </div>

                                {/* Body */}
                                <div className="flex flex-col flex-grow px-6 py-4 overflow-y-auto max-h-[60vh]">
                                        <div className="pb-4">
                                                <p className="text-sm text-[#656669] font-normal">
                                                        Unit Commercial <span className="text-red-500">*</span>
                                                </p>
                                                <CustomSelect
                                                        options={options}
                                                        placeholder={placeholder || 'Select an option'}
                                                        onChange={(value) => setSelectedOption(value)}
                                                        value={selectedOption}
                                                        defaultValue={options.find((option) => option.value === 'PSWR')}
                                                        required
                                                        className="w-full pb-2"
                                                />
                                        </div>
                                        <label className="text-sm text-[#656669] font-normal pb-2">{labelHeading}</label>
                                        <div className="border border-[#D3D3D4] rounded-md bg-white mt-2">
                                                {/* Search Input */}
                                                <div className="relative p-3">
                                                        <input
                                                                type="text"
                                                                placeholder="Search Package Description"
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                                className="w-full border border-[#D3D3D4] bg-[#FBFBFB] rounded-lg py-2 pl-10 text-sm"
                                                        />
                                                        <SearchOutlined className="absolute left-6 top-1/2 transform -translate-y-1/2 text-[#656669]" />
                                                </div>

                                                {/* Header Row */}
                                                <div className="flex font-semibold text-[#585B61] text-sm px-3 py-2">
                                                        <div className="w-8"></div>
                                                        <div className="w-16">CODE</div>
                                                        <div className="flex-1">DESCRIPTION</div>
                                                        <div className="w-32">TYPE CODE</div>
                                                        <div className="w-32">TYPE</div>
                                                </div>

                                                {/* Package List */}
                                                {/* Package List */}
                                                <div className="max-h-[200px] overflow-y-auto">
                                                        {loading ? (
                                                                <Loader />
                                                        ) : filteredPackages.length > 0 ? (
                                                                filteredPackages.map((pkg) => (
                                                                        <div
                                                                                key={pkg.PACKAGE_ID}
                                                                                className="flex items-center text-sm px-3 py-2">
                                                                                <div className="w-8">
                                                                                        <input
                                                                                                type="checkbox"
                                                                                                checked={selectedPackages.some((p) => p.PACKAGE_ID === pkg.PACKAGE_ID)}
                                                                                                onChange={(e) => handleCheckboxChange(e, pkg)}
                                                                                                className={styles.customCheckbox}
                                                                                        />
                                                                                </div>
                                                                                <div className="w-16">{pkg.PACKAGE_ID}</div>
                                                                                <div className="flex-1">{pkg.PACKAGE_NAME}</div>
                                                                                <div className="w-32">{pkg.PACKAGE_TYPE_CODE}</div>
                                                                                <div className="w-32">{pkg.PACKAGE_TYPE}</div>
                                                                        </div>
                                                                ))
                                                        ) : (
                                                                <div className="text-center py-4 text-gray-400 text-sm">No packages found.</div>
                                                        )}
                                                </div>
                                        </div>

                                        {/* Summary Text */}
                                        <div className="mt-3 text-sm text-[#94969C] font-semibold">{selectedPackages.length ? `${selectedPackages.length} Packages selected (${selectedPackages.map((pkg) => pkg.PACKAGE_NAME).join(', ')}).` : ''}</div>
                                </div>

                                {/* Footer */}
                                <div className="border-t bg-[#F5F6F5] px-6 py-4 flex justify-end space-x-2 rounded-b-2xl">
                                        <Button
                                                text="Cancel"
                                                variant="secondary"
                                                onClick={handleClose}
                                        />
                                        <Button
                                                text="Submit"
                                                onClick={handleSubmit}
                                                variant={'primary'}
                                                disabled={selectedPackages.length === 0 || !selectedOption}
                                        />
                                </div>
                        </div>
                </div>
        );
};

export default memo(CheckboxModel);
